import React, { type FC, useState } from 'react'

import type { ReadonlyDate } from '../../../../utils'

import { useIntervalEffect } from '../../Interval'

/**
 * Object with a set of instructions to convert a timespan into countdown digits
 */
const timeUnits = [
    { ignore: true, size: 1_000 },
    { ignore: false, size: 60 },
    { ignore: false, size: 60 },
    { ignore: false, size: 24 },
]

const calculateRelativeTimeUnits = (value: ReadonlyDate, unitsPaddingCount: number): readonly number[] => {
    let timeLeft = value.getTime() - Date.now()
    const units: number[] = []

    for (const { size, ignore } of timeUnits) {
        if (timeLeft <= 0) {
            break
        }

        const unitValue = timeLeft % size

        if (!ignore) {
            /** Value must no be ignored */
            units.unshift(unitValue)
        }

        timeLeft = (timeLeft - unitValue) / size
    }

    const count = Math.max(units.length, unitsPaddingCount)

    const paddedUnits = [...Array(count).fill(0), ...units]
    return paddedUnits.slice(paddedUnits.length - count, paddedUnits.length)
}

/**
 * Renders a simple countdown, given date **must** be in the future
 */
export const Countdown: FC<
    Readonly<{
        value: ReadonlyDate
        /**
         * How many units must always be displayed
         * Passing a 3 here would turn "10 seconds" into "00:00:03"
         */
        unitsPaddingCount?: number
    }>
> = ({ value, unitsPaddingCount = 2 }) => {
    const [units, setUnits] = useState(() => calculateRelativeTimeUnits(value, unitsPaddingCount))
    useIntervalEffect(() => setUnits(calculateRelativeTimeUnits(value, unitsPaddingCount)), 1_000, [value.getTime(), unitsPaddingCount])

    return <>{units.map((part) => String(part).padStart(2, '0')).join(':')}</>
}
